// Pursuit list + create.
//
// Routed at /capture. Lists the tenant's pursuits (deal threads) in either a
// sortable/filterable table or a tile grid. Selecting a row/card opens the
// shaping home at /capture/:id. No mock data — an empty tenant shows the empty
// state.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, SpecraOverlayDialog, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { LayoutGrid, Table as TableIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageBody, PageHero } from '../../components/layout';
import { useFeatureFlag, useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { EntityTable } from '../Delivery/_shared/components/EntityTable';
import { EmptyState } from '../Delivery/_shared/components/PageStates';
import { applyTableView } from '../Delivery/_shared/lib/tableSortFilter';
import { formatTs, STAGES, tsMs } from './constants';
import { useCaptureProjects, useCreateCaptureProject } from './hooks';
const CAPTURE_SUBTITLE = 'Deal threads — shaping, touchpoints, workstreams, and go/no-go. Not a kanban.';
// Stage options for the list filter include `converted` (unlike the editor,
// which can't set it) so a converted pursuit can still be filtered for here.
const LIST_STAGE_OPTIONS = [
    ...STAGES,
    {
        value: 'converted',
        label: 'Converted'
    }
];
const STAGE_STYLE = {
    shaping: 'bg-blue-50 text-blue-700',
    qualifying: 'bg-indigo-50 text-indigo-700',
    pursuing: 'bg-amber-50 text-amber-700',
    no_bid: 'bg-gray-100 text-gray-600',
    converted: 'bg-green-50 text-green-700'
};
function StageBadge({ stage }) {
    return /*#__PURE__*/ _jsx("span", {
        className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STAGE_STYLE[stage] ?? STAGE_STYLE.shaping}`,
        children: stage
    });
}
const LIST_COLUMNS = [
    {
        key: 'name',
        header: 'Name',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'stage',
        header: 'Stage',
        sortable: true,
        filter: 'select',
        filterOptions: LIST_STAGE_OPTIONS,
        render: (r)=>/*#__PURE__*/ _jsx(StageBadge, {
                stage: r.stage
            })
    },
    {
        key: 'updatedAt',
        header: 'Updated',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.updatedAt),
        render: (r)=>formatTs(r.updatedAt)
    }
];
// Sort options offered in tile view (table view sorts via column headers).
const TILE_SORTS = [
    {
        value: 'name',
        label: 'Name'
    },
    {
        value: 'stage',
        label: 'Stage'
    },
    {
        value: 'updatedAt',
        label: 'Updated'
    }
];
export function CaptureListPage() {
    const navigate = useNavigate();
    const { data: captures, isLoading } = useCaptureProjects();
    const create = useCreateCaptureProject();
    const prefs = useTablePreferences('ts:capture:list', {
        view: 'table'
    });
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [hypothesis, setHypothesis] = useState('');
    // Specra redesign: inline PageHero in place of the bespoke serif header.
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_capture');
    const redesign = flagsReady && redesignEnabled;
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    const setShowBreadcrumbs = useLayoutStore((s)=>s.setShowBreadcrumbs);
    useEffect(()=>{
        if (!redesign) return;
        setShowHeader(false);
        setShowBreadcrumbs(false);
        return ()=>{
            setShowHeader(true);
            setShowBreadcrumbs(true);
        };
    }, [
        redesign,
        setShowHeader,
        setShowBreadcrumbs
    ]);
    const submit = ()=>{
        if (!name.trim()) return;
        create.mutate({
            name: name.trim(),
            opportunityHypothesis: hypothesis.trim()
        }, {
            onSuccess: (summary)=>{
                setOpen(false);
                setName('');
                setHypothesis('');
                navigate(`/capture/${summary.captureProjectId}`);
            }
        });
    };
    const createDialog = /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: setOpen,
        title: "New Pursuit",
        testId: Testids.capture.createDialog,
        trigger: /*#__PURE__*/ _jsx(SpecraButton, {
            "data-testid": Testids.capture.newButton,
            children: "New Pursuit"
        }),
        footerProps: {
            confirmLabel: create.isPending ? 'Creating…' : 'Create',
            onConfirm: submit,
            confirmProps: {
                disabled: !name.trim() || create.isPending,
                'data-testid': Testids.capture.createSubmitButton
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4 py-2",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "capture-name",
                            children: "Name"
                        }),
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            id: "capture-name",
                            value: name,
                            onChange: (e)=>setName(e.target.value),
                            placeholder: "Agency X recompete",
                            "data-testid": Testids.capture.createNameInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "capture-hypothesis",
                            children: "Opportunity hypothesis"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "capture-hypothesis",
                            value: hypothesis,
                            onChange: (e)=>setHypothesis(e.target.value),
                            placeholder: "Why this is worth pursuing…",
                            "data-testid": Testids.capture.createHypothesisInput
                        })
                    ]
                })
            ]
        })
    });
    const rows = captures ?? [];
    // Tile view applies the same persisted sort/filter as the table.
    const tileRows = applyTableView(rows, LIST_COLUMNS, prefs.sort, prefs.filters);
    // Toolbar: view toggle (+ tile-mode filter/sort, since table mode has those
    // inline in its header). Shared by both the redesign and legacy layouts.
    const toolbar = rows.length > 0 && /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "inline-flex overflow-hidden rounded-md border",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: prefs.view === 'table' ? 'secondary' : 'ghost',
                        size: "sm",
                        className: "rounded-none",
                        "data-testid": Testids.capture.viewToggleTable,
                        onClick: ()=>prefs.setView('table'),
                        iconLeft: /*#__PURE__*/ _jsx(TableIcon, {
                            className: "h-4 w-4"
                        }),
                        children: "Table"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: prefs.view === 'tile' ? 'secondary' : 'ghost',
                        size: "sm",
                        className: "rounded-none",
                        "data-testid": Testids.capture.viewToggleTile,
                        onClick: ()=>prefs.setView('tile'),
                        iconLeft: /*#__PURE__*/ _jsx(LayoutGrid, {
                            className: "h-4 w-4"
                        }),
                        children: "Tiles"
                    })
                ]
            }),
            prefs.view === 'tile' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Select, {
                        value: prefs.filters.stage || '__all__',
                        onValueChange: (v)=>prefs.setFilter('stage', v === '__all__' ? '' : v),
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "h-9 w-[160px]",
                                "data-testid": Testids.capture.listStageFilter,
                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                    placeholder: "All stages"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(SelectContent, {
                                children: [
                                    /*#__PURE__*/ _jsx(SelectItem, {
                                        value: "__all__",
                                        children: "All stages"
                                    }),
                                    LIST_STAGE_OPTIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                            value: o.value,
                                            children: o.label
                                        }, o.value))
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        value: prefs.sort?.key ?? '',
                        onValueChange: (v)=>prefs.setSort({
                                key: v,
                                dir: prefs.sort?.dir ?? 'asc'
                            }),
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "h-9 w-[150px]",
                                "data-testid": Testids.capture.listSortSelect,
                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                    placeholder: "Sort by…"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: TILE_SORTS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        ]
                    }),
                    prefs.sort && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: ()=>prefs.toggleSort(prefs.sort?.key ?? 'name'),
                        children: prefs.sort.dir === 'asc' ? 'Asc' : 'Desc'
                    })
                ]
            })
        ]
    });
    const list = isLoading ? /*#__PURE__*/ _jsx("div", {
        className: "text-sm text-muted-foreground",
        children: "Loading pursuits…"
    }) : rows.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
        title: "No pursuits yet",
        description: "Open a Pursuit to start shaping — workstreams, touchpoints, and go/no-go live here, not in a General project."
    }) : prefs.view === 'table' ? /*#__PURE__*/ _jsx(EntityTable, {
        data: rows,
        columns: LIST_COLUMNS,
        testid: Testids.capture.listTable,
        rowTestid: Testids.capture.listRow,
        rowKey: (r)=>r.captureProjectId,
        onRowClick: (r)=>navigate(`/capture/${r.captureProjectId}`),
        sort: prefs.sort,
        onToggleSort: prefs.toggleSort,
        filters: prefs.filters,
        onFilterChange: prefs.setFilter
    }) : /*#__PURE__*/ _jsx("div", {
        className: "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3",
        children: tileRows.map((c)=>/*#__PURE__*/ _jsxs(SpecraCard, {
                className: "cursor-pointer transition-colors hover:border-primary",
                "data-testid": Testids.capture.listCard,
                "data-capture-id": c.captureProjectId,
                onClick: ()=>navigate(`/capture/${c.captureProjectId}`),
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        className: "pb-2",
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            className: "text-base",
                            children: c.name
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx(StageBadge, {
                                        stage: c.stage
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs text-muted-foreground",
                                        children: [
                                            "Updated ",
                                            formatTs(c.updatedAt)
                                        ]
                                    })
                                ]
                            }),
                            c.opportunityHypothesis ? /*#__PURE__*/ _jsx("p", {
                                className: "line-clamp-2 text-sm text-muted-foreground",
                                children: c.opportunityHypothesis
                            }) : null
                        ]
                    })
                ]
            }, c.captureProjectId))
    });
    if (redesign) {
        return /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Pursuits",
                    subtitle: CAPTURE_SUBTITLE,
                    actions: createDialog
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-6",
                    "data-testid": Testids.capture.listRoot,
                    children: [
                        toolbar,
                        list
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4 px-6 py-4",
            "data-testid": Testids.capture.listRoot,
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "text-2xl font-semibold",
                                    children: "Pursuits"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-sm text-muted-foreground",
                                    children: CAPTURE_SUBTITLE
                                })
                            ]
                        }),
                        createDialog
                    ]
                }),
                toolbar,
                list
            ]
        })
    });
}
